import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { Users, Plus, Trash2, ArrowLeft, UserPlus, X, Save, Link2, Unlink, Globe, EyeOff, RotateCcw } from 'lucide-react';
import { getTeams, saveTeam, deleteTeam, SavedTeam, SavedTeamPlayer } from '../utils/storage';
import { useAuth } from '../auth/AuthContext';
import { fetchTeamCapacity, TeamCapacity } from '../api/teamCapacity';

export function TeamManagement() {
  const { activeAccountId } = useAuth();
  const [teams, setTeams] = useState<SavedTeam[]>([]);
  const [capacity, setCapacity] = useState<TeamCapacity | null>(null);
  const [showNewTeamForm, setShowNewTeamForm] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [deletingTeamId, setDeletingTeamId] = useState<string | null>(null);

  // Form state
  const [teamName, setTeamName] = useState('');
  const [players, setPlayers] = useState<SavedTeamPlayer[]>([{ displayName: '' }, { displayName: '' }]);
  const [discoverable, setDiscoverable] = useState(true);

  // Which player row has its "link to an existing user" email field open, if any.
  const [linkingIndex, setLinkingIndex] = useState<number | null>(null);
  const [linkEmailDraft, setLinkEmailDraft] = useState('');

  const playerInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    loadTeams();
  }, []);

  useEffect(() => {
    if (!activeAccountId) return;
    let cancelled = false;
    fetchTeamCapacity(activeAccountId)
      .then(result => { if (!cancelled) setCapacity(result); })
      .catch(error => console.error('Failed to load team capacity:', error));
    return () => { cancelled = true; };
  }, [activeAccountId]);

  const loadTeams = async () => {
    try {
      setTeams(await getTeams());
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const handleSaveTeam = async () => {
    if (!teamName.trim()) {
      alert('Please enter a team name');
      return;
    }

    const validPlayers = players.filter(p => p.displayName.trim());
    if (validPlayers.length < 2) {
      alert('Please add at least 2 players');
      return;
    }

    const team: SavedTeam = {
      id: editingTeamId || Date.now().toString(),
      name: teamName.trim(),
      players: validPlayers,
      createdAt: editingTeamId ? teams.find(t => t.id === editingTeamId)?.createdAt || Date.now() : Date.now(),
      discoverable,
      archived: false,
    };

    try {
      await saveTeam(team);
      await loadTeams();
      resetForm();
    } catch (error) {
      console.error('Failed to save team:', error);
      alert('Could not save the team. Please try again — if you just linked a player, check the email address is registered.');
    }
  };

  const handleEditTeam = (team: SavedTeam) => {
    setEditingTeamId(team.id);
    setTeamName(team.name);
    setPlayers(team.players);
    setDiscoverable(team.discoverable);
    setShowNewTeamForm(true);
  };

  const handleDeleteTeam = (id: string) => {
    setDeletingTeamId(id);
  };

  const confirmDeleteTeam = async () => {
    if (deletingTeamId) {
      try {
        const target = teams.find(t => t.id === deletingTeamId);
        if (target?.discoverable) {
          // Archives rather than removes — go through the normal upsert path so the
          // offline mirror reflects the change immediately, rather than the delete
          // path's optimistic local removal, which would make the team vanish here
          // until the next sync pulls it back in archived.
          await saveTeam({ ...target, archived: true });
        } else {
          await deleteTeam(deletingTeamId);
        }
        await loadTeams();
      } catch (error) {
        console.error('Failed to delete team:', error);
      }
    }
    setDeletingTeamId(null);
  };

  const handleRestoreTeam = async (team: SavedTeam) => {
    try {
      await saveTeam({ ...team, archived: false });
      await loadTeams();
    } catch (error) {
      console.error('Failed to restore team:', error);
    }
  };

  const resetForm = () => {
    setTeamName('');
    setPlayers([{ displayName: '' }, { displayName: '' }]);
    setDiscoverable(true);
    setShowNewTeamForm(false);
    setEditingTeamId(null);
    setLinkingIndex(null);
    setLinkEmailDraft('');
  };

  const updatePlayerName = (index: number, displayName: string) => {
    setPlayers(players.map((p, i) => (i === index ? { ...p, displayName } : p)));
  };

  const startLinking = (index: number) => {
    setLinkingIndex(index);
    setLinkEmailDraft('');
  };

  const confirmLink = (index: number) => {
    const email = linkEmailDraft.trim();
    if (!email) return;
    // Resolved server-side on save; shown as pending here until then.
    setPlayers(players.map((p, i) => (i === index ? { ...p, linkedUserEmail: email } : p)));
    setLinkingIndex(null);
    setLinkEmailDraft('');
  };

  const unlink = (index: number) => {
    setPlayers(players.map((p, i) =>
      i === index ? { ...p, linkedUserEmail: undefined, linkedUserId: null, linkedUserDisplayName: null } : p
    ));
  };

  // Program teams are deliberately created rosters, discoverable by other programs.
  // Opponents are the byproduct of just typing an unfamiliar name during game setup —
  // kept in their own list rather than mixed in, since they're not vetted the same way.
  const programTeams = teams.filter(t => t.discoverable && !t.archived);
  const opponentTeams = teams.filter(t => !t.discoverable && !t.archived);
  const archivedTeams = teams.filter(t => t.archived);

  const renderTeamCard = (team: SavedTeam) => (
    <div
      key={team.id}
      className={
        team.discoverable
          ? 'bg-blue-500/10 backdrop-blur-sm border border-blue-400/30 rounded-2xl shadow-xl shadow-black/20 p-6'
          : 'bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6'
      }
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1">{team.name}</h3>
          {team.discoverable ? (
            <span
              title="Other programs can find this team when setting up a game"
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-300"
            >
              <Globe className="w-3.5 h-3.5" />
              Program Team
            </span>
          ) : (
            <span
              title="Only your program can see this team"
              className="inline-flex items-center gap-1 text-xs text-blue-300/50"
            >
              <EyeOff className="w-3.5 h-3.5" />
              Opponent
            </span>
          )}
        </div>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-blue-300 mb-2">Players:</p>
        <ul className="space-y-1">
          {team.players.map((player, index) => (
            <li key={index} className="text-sm text-white flex items-center gap-2">
              <span className="w-6 h-6 flex items-center justify-center bg-white/10 rounded-full text-xs font-medium text-blue-300">
                {index + 1}
              </span>
              {player.displayName}
              {player.linkedUserDisplayName && (
                <span
                  title={`Linked to ${player.linkedUserDisplayName}`}
                  className="flex items-center gap-1 text-xs text-emerald-300"
                >
                  <Link2 className="w-3 h-3" />
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2 pt-4 border-t border-white/15">
        <button
          onClick={() => handleEditTeam(team)}
          className="flex-1 bg-blue-500/20 text-blue-300 px-4 py-2 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-medium"
        >
          Edit
        </button>
        <button
          onClick={() => handleDeleteTeam(team.id)}
          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5 text-red-400" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">Team Management</h1>
          <div className="flex gap-3">
            {showNewTeamForm ? (
              <button
                onClick={resetForm}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Team Management
              </button>
            ) : (
              <Link
                to="/program"
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Program Management
              </Link>
            )}
            {!showNewTeamForm && (
              <button
                onClick={() => setShowNewTeamForm(true)}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                New Team
              </button>
            )}
          </div>
        </div>

        {showNewTeamForm && (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">
                {editingTeamId ? 'Edit Team' : 'Create New Team'}
              </h2>
              <button
                onClick={resetForm}
                className="text-blue-300 hover:text-blue-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-2 text-sm text-blue-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={discoverable}
                  onChange={(e) => setDiscoverable(e.target.checked)}
                  className="mt-0.5 accent-blue-500"
                />
                <span>
                  This team is part of the program.
                  <span className="block text-xs text-blue-300/70">
                    Other programs can find it by name and reuse its roster — never gives
                    them access to your games or stats.
                  </span>
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-1">Team Name</label>
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                  placeholder="Enter team name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Players (2-5 players)</label>
                <p className="text-xs text-blue-300/70 mb-2">
                  Optionally link a slot to a registered user — purely so that person can
                  later filter Game History to their own games.
                </p>
                <div className="space-y-2">
                  {players.map((player, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex gap-2">
                        <input
                          ref={(el) => (playerInputRefs.current[index] = el)}
                          type="text"
                          value={player.displayName}
                          onChange={(e) => updatePlayerName(index, e.target.value)}
                          className="flex-1 bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                          placeholder={`Player ${index + 1}`}
                        />
                        {players.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setPlayers(players.filter((_, i) => i !== index))}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>

                      {linkingIndex === index ? (
                        <div className="flex gap-2 pl-1">
                          <input
                            type="email"
                            autoFocus
                            value={linkEmailDraft}
                            onChange={(e) => setLinkEmailDraft(e.target.value)}
                            placeholder="Their registered email address"
                            className="flex-1 bg-white/10 border border-white/20 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400/60 placeholder:text-blue-300/50"
                          />
                          <button
                            type="button"
                            onClick={() => confirmLink(index)}
                            disabled={!linkEmailDraft.trim()}
                            className="px-3 py-1.5 bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                          >
                            Link
                          </button>
                          <button
                            type="button"
                            onClick={() => setLinkingIndex(null)}
                            className="px-2 text-blue-300/70 hover:text-white text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : player.linkedUserEmail ? (
                        <div className="flex items-center gap-2 pl-1 text-xs text-amber-300">
                          <Link2 className="w-3.5 h-3.5" />
                          Will link to {player.linkedUserEmail} on save
                          <button type="button" onClick={() => unlink(index)} className="text-red-300 hover:text-red-200">
                            Cancel
                          </button>
                        </div>
                      ) : player.linkedUserDisplayName ? (
                        <div className="flex items-center gap-2 pl-1 text-xs text-emerald-300">
                          <Link2 className="w-3.5 h-3.5" />
                          Linked to {player.linkedUserDisplayName}
                          <button type="button" onClick={() => unlink(index)} className="flex items-center gap-1 text-red-300 hover:text-red-200">
                            <Unlink className="w-3 h-3" />
                            Unlink
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startLinking(index)}
                          className="flex items-center gap-1 pl-1 text-xs text-blue-300 hover:text-blue-200 transition-colors"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                          Link to existing user
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setPlayers([...players, { displayName: '' }]);
                      setTimeout(() => {
                        playerInputRefs.current[players.length]?.focus();
                      }, 0);
                    }}
                    disabled={players.length >= 5}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-white/20 rounded-lg hover:border-blue-400/60 hover:bg-blue-500/10 transition-colors text-sm text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-white/20 disabled:hover:bg-transparent"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Player
                  </button>
                </div>
              </div>

              <button
                onClick={handleSaveTeam}
                className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white py-2 rounded-lg transition-colors"
              >
                <Save className="w-5 h-5" />
                {editingTeamId ? 'Update Team' : 'Save Team'}
              </button>
            </div>
          </div>
        )}

        {showNewTeamForm ? null : teams.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Users className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Teams Yet</h3>
            <p className="text-blue-300 mb-6">Create teams to quickly start new games</p>
            <button
              onClick={() => setShowNewTeamForm(true)}
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create First Team
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {programTeams.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-lg font-semibold text-white">Program Teams</h2>
                  {capacity?.enforced && (
                    <span className="text-lg font-semibold text-blue-300/70">
                      Team capacity: {capacity.capacity}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {programTeams.map(team => renderTeamCard(team))}
                </div>
              </div>
            )}

            {opponentTeams.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">Opponents</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {opponentTeams.map(team => renderTeamCard(team))}
                </div>
              </div>
            )}

            {archivedTeams.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-white mb-1">Archived Teams</h2>
                <p className="text-sm text-blue-300/70 mb-4">
                  Retired program teams — kept for the record, no longer selectable when
                  setting up a game or findable by other programs.
                </p>
                <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                  <table className="w-full text-sm">
                    <tbody>
                      {archivedTeams.map((team, index) => (
                        <tr
                          key={team.id}
                          className={index > 0 ? 'border-t border-white/10' : ''}
                        >
                          <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                            {team.name}
                          </td>
                          <td className="px-4 py-3 text-blue-300/70 w-full">
                            {team.players.map(p => p.displayName).join(', ')}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => handleRestoreTeam(team)}
                              className="inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 text-sm font-medium transition-colors"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delete/Archive Confirmation Modal */}
        {deletingTeamId && (() => {
          const target = teams.find(t => t.id === deletingTeamId);
          const willArchive = target?.discoverable ?? false;

          return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <div className="bg-blue-950 border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 max-w-md w-full mx-4">
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {willArchive ? 'Archive Team' : 'Delete Team'}
                    </h3>
                    <p className="text-blue-300">
                      {willArchive ? (
                        <>
                          <strong className="text-white">{target?.name}</strong> will move to
                          Archived Teams — no longer selectable for a new game or findable by
                          other programs, but kept for the record.
                        </>
                      ) : (
                        <>
                          Are you sure you want to delete{' '}
                          <strong className="text-white">{target?.name}</strong>? This action
                          cannot be undone.
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeletingTeamId(null)}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteTeam}
                    className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {willArchive ? 'Archive Team' : 'Delete Team'}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
